import { useCallback, useMemo } from 'react';
import { Badge, Center, Loader, SimpleGrid, Stack } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useReport } from '@/api/reports';
import { reportViewerLink, reportViewerToken } from '@/api/viewer';
import { EmptyState } from '@/components/EmptyState';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { layerRefs, ReportPage, toReportView } from '@/features/reports/ReportPage';
import { useReportViewer } from '@/features/reports/useReportViewer';
import { ReportSettingsDrawer } from '@/features/reports/ReportSettingsDrawer';
import { ReportInputsCard } from '@/features/reports/ReportInputsCard';
import { ShareLinkCard } from '@/features/reports/ShareLinkCard';
import { InternalComments } from '@/features/staff/InternalComments';
import { usePageTitle } from '@/components/usePageTitle';
import { STATUS_COLORS } from '@/theme/colors';

export const Route = createFileRoute('/_staff/staff/reports/$reportId')({ component: StaffReportRoute });

function StaffReportRoute() {
  const { t } = useTranslation('reports');
  const { reportId } = Route.useParams();
  const navigate = useNavigate();
  const report = useReport(reportId);
  const fetchToken = useCallback(() => reportViewerToken(reportId), [reportId]);
  const fetchLink = useCallback((object: string, path: string) => reportViewerLink(reportId, object, path), [reportId]);
  const { source, linkSource, credential, error: tokenError } = useReportViewer(fetchToken, fetchLink);
  const view = useMemo(() => (report.data ? toReportView(report.data) : null), [report.data]);
  const refs = useMemo(() => (view ? layerRefs(credential?.objects, view) : []), [credential, view]);
  usePageTitle(report.data?.name || t('title'));

  if (report.isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (report.isError || !view) return <EmptyState title={errorMessage(report.error)} />;

  return (
    <Stack gap="lg">
      <StaffBreadcrumbs projectId={report.data.project} current={report.data.name || t('title')} />
      <ReportPage
        report={view}
        refs={refs}
        tokenSource={source}
        linkSource={linkSource}
        granted={credential?.objects}
        fetchLink={fetchLink}
        tokenError={!!tokenError}
        viewKey={reportId}
        actions={
          <>
            {!report.data.visible_to_client && (
              <Badge
                variant="outline"
                fz={12}
                styles={{ root: { color: STATUS_COLORS.muted.color, backgroundColor: STATUS_COLORS.muted.bg, borderColor: STATUS_COLORS.muted.border } }}
              >
                {t('page.hidden')}
              </Badge>
            )}
            <ReportSettingsDrawer report={report.data} />
          </>
        }
        editable
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <ShareLinkCard report={report.data} staff onDeleted={() => void navigate({ to: '/staff/projects' })} />
        <ReportInputsCard report={report.data} />
      </SimpleGrid>
      <InternalComments subject={reportId} />
    </Stack>
  );
}
